class DomInjector {
    constructor() {
        // Пытаемся найти контейнер для постов, если это архив
        this.container = document.querySelector('.posts-container') || 
                         document.querySelector('.posts-loop') || 
                         document.querySelector('.row') || 
                         document.querySelector('main') || 
                         document.body;
        
        this.initialUrl = window.location.href;
        this.historyUrls = new Set([this.initialUrl]);
    }

    inject(html, meta) {
        if (!html) return null;

        const wrapper = document.createElement('div');
        // Если это карточка, мы не оборачиваем её в infinite-item, 
        // так как структура карточки обычно уже полная.
        // Но для single контента обертка нужна.
        const isCard = !meta.url || meta.url === '';
        
        wrapper.className = isCard ? 'infinite-card-wrapper' : 'infinite-item ic-level-block';
        wrapper.style.position = 'relative'; 

        if (meta.id) wrapper.setAttribute('data-id', meta.id);
        if (meta.url) wrapper.setAttribute('data-url', meta.url);
        if (meta.title) wrapper.setAttribute('data-title', meta.title);
        if (meta.type) wrapper.setAttribute('data-type', meta.type);

        // Store full meta for header updates
        wrapper.setAttribute('data-meta', JSON.stringify(meta));

        wrapper.innerHTML = html;

        // Если это карточка и у нас есть контейнер-сетка, 
        // вставляем содержимое враппера напрямую или сам враппер
        this.container.appendChild(wrapper);

        this.executeScripts(wrapper);
        return wrapper;
    }

    injectBatch(html) {
        if (!html) return;

        // Create a temporary container to parse the HTML string
        const temp = document.createElement('div');
        temp.innerHTML = html;
        
        const fragment = document.createDocumentFragment();
        const addedElements = [];

        // Move children from temp to fragment
        while (temp.firstChild) {
            const child = temp.firstChild;
            // Only move element nodes or non-empty text nodes if needed
            // But usually we just take everything
            addedElements.push(child);
            fragment.appendChild(child);
        }
        
        this.container.appendChild(fragment);

        // Execute scripts if any (unlikely for cards but safe to have)
        addedElements.forEach(el => {
            if (el.nodeType === 1) { // Element node
                this.executeScripts(el);
            }
        });

        return addedElements;
    }

    updateHistory(url, title, meta = null) {
        if (window.location.href !== url) {
            // Проверяем, находимся ли мы на самой первой странице (landing page)
            // Мы ориентируемся на state.url, который мы установили в index.js
            const isAtInitialEntry = history.state && history.state.url === this.initialUrl;

            // Если мы на первой странице и скроллим куда-то еще, мы ОБЯЗАТЕЛЬНО делаем pushState,
            // чтобы не перезаписать входную точку в историю (landing page).
            if (isAtInitialEntry || !this.historyUrls.has(url)) {
                history.pushState({ url: url, title: title }, title, url);
                this.historyUrls.add(url);
            } else {
                // Если мы скроллим назад (или между уже виденными страницами не из начала), используем replaceState
                history.replaceState({ url: url, title: title }, title, url);
            }

            document.title = title;

            if (meta) {
                this.updateHeader(meta);
            }
        }
    }

    updateHeader(meta) {
        if (!meta) return;

        // 1. Clear "Ghost" Meta Tags (Clean Slate approach)
        this.clearManagedMeta();

        // 2. Canonical
        if (meta.canonical) {
            let canonical = document.querySelector('link[rel="canonical"]');
            if (!canonical) {
                canonical = document.createElement('link');
                canonical.setAttribute('rel', 'canonical');
                document.head.appendChild(canonical);
            }
            canonical.setAttribute('href', meta.canonical);
        }

        // 3. Hreflang alternates
        this.updateHreflang(meta.hreflang);

        // 4. Description
        if (meta.description) {
            let desc = document.querySelector('meta[name="description"]');
            if (!desc) {
                desc = document.createElement('meta');
                desc.setAttribute('name', 'description');
                document.head.appendChild(desc);
            }
            desc.setAttribute('content', meta.description);
        }

        // 5. OpenGraph & Twitter
        if (meta.og) {
            Object.entries(meta.og).forEach(([prop, val]) => {
                if (!val) return;

                let tag = document.querySelector(`meta[property="${prop}"]`) || 
                          document.querySelector(`meta[name="${prop}"]`);
                
                if (!tag) {
                    tag = document.createElement('meta');
                    if (prop.startsWith('og:') || prop.startsWith('article:')) {
                        tag.setAttribute('property', prop);
                    } else {
                        tag.setAttribute('name', prop);
                    }
                    document.head.appendChild(tag);
                }
                tag.setAttribute('content', val);
            });
        }

        // 6. Schema JSON-LD (Strict ID targeting)
        if (meta.schema) {
            let schemaScript = document.getElementById('saborlista-schema-graph');
            
            // If not found, try to find any schema script as fallback or create one
            if (!schemaScript) {
                schemaScript = document.querySelector('script[type="application/ld+json"]');
                if (schemaScript) schemaScript.id = 'saborlista-schema-graph';
            }

            if (!schemaScript) {
                schemaScript = document.createElement('script');
                schemaScript.type = 'application/ld+json';
                schemaScript.id = 'saborlista-schema-graph';
                document.head.appendChild(schemaScript);
            }

            schemaScript.textContent = JSON.stringify({
                '@context': 'https://schema.org',
                '@graph': meta.schema
            });
        }
    }

    /**
     * Replaces all hreflang alternate links with entries from the current post meta.
     * Removes existing tags when the new post has no language alternatives.
     *
     * @param {Array<{lang: string, url: string}>|undefined} entries
     */
    updateHreflang(entries) {
        document.querySelectorAll('link[rel="alternate"][hreflang]').forEach(tag => tag.remove());

        if (!Array.isArray(entries) || entries.length === 0) {
            return;
        }

        const canonical = document.querySelector('link[rel="canonical"]');
        let insertAfter = canonical;

        entries.forEach(entry => {
            if (!entry || !entry.lang || !entry.url) return;

            const link = document.createElement('link');
            link.setAttribute('rel', 'alternate');
            link.setAttribute('hreflang', entry.lang);
            link.setAttribute('href', entry.url);

            if (insertAfter && insertAfter.parentNode) {
                insertAfter.parentNode.insertBefore(link, insertAfter.nextSibling);
                insertAfter = link;
            } else {
                document.head.appendChild(link);
                insertAfter = link;
            }
        });
    }

    /**
     * Removes or clears all metadata tags that we manage to avoid "ghost" content 
     * from previous posts (e.g. recipe schema data staying on a regular post).
     */
    clearManagedMeta() {
        // List of prefixes we manage
        const prefixes = ['og:', 'twitter:', 'article:'];
        
        // 1. Remove all meta tags with these prefixes
        const metaTags = document.querySelectorAll('meta');
        metaTags.forEach(tag => {
            const property = tag.getAttribute('property') || '';
            const name = tag.getAttribute('name') || '';
            
            const isManaged = prefixes.some(p => property.startsWith(p) || name.startsWith(p));
            if (isManaged) {
                tag.remove();
            }
        });

        // 2. Clear description and canonical for clean overwrite
        const desc = document.querySelector('meta[name="description"]');
        if (desc) desc.setAttribute('content', '');
        
        const canonical = document.querySelector('link[rel="canonical"]');
        if (canonical) canonical.setAttribute('href', '');

        // 3. Remove hreflang tags (recreated in updateHreflang)
        document.querySelectorAll('link[rel="alternate"][hreflang]').forEach(tag => tag.remove());
    }

    executeScripts(element) {
        const scripts = element.querySelectorAll('script');
        scripts.forEach(oldScript => {
            const newScript = document.createElement('script');
            Array.from(oldScript.attributes).forEach(attr => newScript.setAttribute(attr.name, attr.value));
            newScript.appendChild(document.createTextNode(oldScript.innerHTML));
            oldScript.parentNode.replaceChild(newScript, oldScript);
        });
    }
}

window.SaborlistaDomInjector = DomInjector;
